import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getNcdRiskReadingList(
    $filter: ncdRiskReadingFilterInput!
    $pagination: paginationInput
  ) {
    getNcdRiskReadingList(filter: $filter, pagination: $pagination) {
      items {
        vincenseReading {
          id
          readingId
          pulseFlag
          pulseColor
          pulseValue
          pulseReferenceRange
          o2Flag
          o2Color
          o2Value
          o2ReferenceRange
          rrFlag
          rrColor
          rrValue
          rrReferenceRange
          skinValue
        }
        subject {
          id
          subjectType
          name
          gender
          dob
        }
        location
        datetime
        id
        bpReading {
          id
          readingId
          systolicFlag
          systolicColor
          systolicValue
          systolicReferenceRange
          diastolicFlag
          diastolicColor
          diastolicValue
          diastolicReferenceRange
          meanFlag
          meanColor
          meanValue
          meanReferenceRange
          captureType
        }
        glucometerReading {
          id
          readingId
          randomFlag
          randomColor
          randomValue
          randomReferenceRange
          fastingFlag
          fastingColor
          fastingValue
          fastingReferenceRange
          ppFlag
          ppColor
          ppValue
          ppReferenceRange
        }
        weighingReading {
          id
          weightValue
          heightValue
          bmiFlag
          bmiColor
          bmiValue
          bmiReferenceRange
          captureType
        }
        ecgReading {
          id
          ecgUrl
          pdfUrl
        }
        ishReading {
          id
          score
          color
          referenceRange
        }
        ewsReading {
          id
          pulseValue
          pulseScore
          pulseColor
          skinValue
          skinScore
          skinColor
          o2Value
          o2Score
          o2Color
          rrValue
          rrScore
          rrColor
          systolicValue
          systolicScore
          systolicColor
          avpuValue
          avpuScore
          avpuColor
          uoValue
          uoScore
          uoColor
          updatedBy {
            id
            name
          }
        }
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.ReadingListState = {
  result: null,
  error: null,
  loading: false
};

export const getNcdRiskReadingListRequest = createAsyncThunk<
  TYPES.ReadingList,
  {
    filter: TYPES.NcdRiskReadingFilterInput;
    pagination?: TYPES.PaginationInput;
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reading/get_ncd_risk_reading_list',
  async (
  params: {
    filter: TYPES.NcdRiskReadingFilterInput;
    pagination?: TYPES.PaginationInput;
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getNcdRiskReadingList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getNcdRiskReadingListSlice = createSlice({
  name: 'get_ncd_risk_reading_list',
  initialState: initialState,
  reducers: {
    getNcdRiskReadingListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getNcdRiskReadingListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getNcdRiskReadingListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getNcdRiskReadingListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getNcdRiskReadingListClear } =
getNcdRiskReadingListSlice.actions;

export const getNcdRiskReadingList = createSelector(
  (state: RootState) => state.reading.getNcdRiskReadingList.result,
  (state: RootState) => state.reading.getNcdRiskReadingList.error,
  (state: RootState) => state.reading.getNcdRiskReadingList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getNcdRiskReadingListSlice.reducer;